import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

// Icon Generator Function
const generateIcon = (index) => {
  const icons = [
    { emoji: "🏦", bg: "bg-green-50", color: "text-green-600" },
    { emoji: "🔧", bg: "bg-blue-50", color: "text-blue-600" },
    { emoji: "🏢", bg: "bg-purple-50", color: "text-purple-600" },
    { emoji: "📄", bg: "bg-amber-50", color: "text-amber-600" },
    { emoji: "👥", bg: "bg-indigo-50", color: "text-indigo-600" },
    { emoji: "💰", bg: "bg-emerald-50", color: "text-emerald-600" },
    { emoji: "📊", bg: "bg-cyan-50", color: "text-cyan-600" },
    { emoji: "💳", bg: "bg-pink-50", color: "text-pink-600" },
    { emoji: "💵", bg: "bg-teal-50", color: "text-teal-600" },
    { emoji: "🛡️", bg: "bg-rose-50", color: "text-rose-600" },
  ];
  
  return icons[index % icons.length];
};

const Uccs = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [uccsData, setUccsData] = useState(null);

  useEffect(() => {
    fetchUccsData();
  }, []);

  const fetchUccsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/uccs/get_uccs_content`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setUccsData(result.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching UCCS data:', error);
      setLoading(false);
    }
  };

  const content = {
    en: {
      title: "Urban Cooperative Credit Societies (UCCs)",
      overview: "Overview",
      coreActivities: "Core Activities",
      keyStats: "Key Statistics",
    },
    ta: {
      title: "நகர கூட்டுறவுக் கடன் சங்கங்கள்",
      overview: "அறிமுகம்",
      coreActivities: "முக்கிய செயல்பாடுகள்",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
    }
  };

  const t = content[language];

  // Static Tamil content (overrides API for Tamil only)
  const staticTamil = {
    overview: "நகரங்கள் மற்றும் புறநகர்ப் பகுதிகளில் வாழும் சிறு வணிகர்கள், கலைஞர்கள் மற்றும் நடுத்தர வருவாய்க் குடும்பங்களைச் சேர்ந்தவர்களுக்கு வைப்புநிதி திரட்டுதல் மற்றும் வீடு, தொழில் மற்றும் வேளாண்மை அல்லாத செயல்பாடுகளுக்குக் கடன் வழங்குதல் உள்ளிட்ட சேவைகளை நகர கூட்டுறவுக் கடன் சங்கங்கள் செய்கின்றன. இவை சமூகம் சார்ந்த நிதி நிறுவனங்களாகச் செயல்படுபவை. மேலும் எளிதில் அணுகப்படுதல் மற்றும் நம்பிக்கை ஆகியவற்றை அடிப்படையாகக் கொண்டு இயங்குபவை.",
    coreActivities: [
      "கடன் மற்றும் வைப்பு சேவைகள்",
      "சிறு வணிகர்கள் மற்றும் கலைஞர்களுக்கான சேவைகள்",
      "வீடு மற்றும் வணிகக் கடன்கள்",
      "வேளாண்மை அல்லாத துறைகளுக்கான கடன் சேவைகள்",
      "நுகர்வோரை மையப்படுத்திய பொருளாதார சேவைகள்",
    ],
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!uccsData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">No UCCS data available</div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 to-green-50 min-h-screen py-8">
      {/* Header */}
      <h1 className="text-4xl font-bold text-center mt-6 mb-8 text-gray-900">
        {t.title}
      </h1>

      {/* Overview Section */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-green-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🏢</span>
            </div>
            {t.overview}
          </h2>
          <div className="border-l-4 border-green-600 pl-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              {language === 'en' ? uccsData.overview_eng : staticTamil.overview}
            </p>
          </div>
        </div>
      </div>

      {/* Core Activities Section */}
      {(() => {
        const activities = language === 'ta'
          ? staticTamil.coreActivities.map((text) => ({ content_tamil: text }))
          : (uccsData.core_activity || []);

        if (activities.length === 0) return null;

        return (
          <div className="max-w-7xl mx-auto px-6 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {t.coreActivities}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activities.map((activity, index) => {
                const iconStyle = generateIcon(index);

                return (
                  <div
                    key={index}
                    className={`${iconStyle.bg} rounded-xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100`}
                  >
                    <div className="text-3xl mb-3">
                      {iconStyle.emoji}
                    </div>
                    <p className="text-gray-800 text-base font-semibold leading-snug">
                      {language === 'en' ? activity.content_eng : activity.content_tamil}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Key Statistics */}
      {uccsData.key_stats && uccsData.key_stats.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t.keyStats}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {uccsData.key_stats.map((stat, index) => {
              const iconStyle = generateIcon(index);
              
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center border border-gray-100"
                >
                  <div className="text-4xl mb-3">{iconStyle.emoji}</div>
                  <div className="font-bold text-green-600 mb-2">
                    <span className="text-3xl">
                      {language === 'en' ? stat.heading_eng : stat.heading_tamil}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-medium leading-tight">
                    {language === 'en' ? stat.content_eng : stat.content_tamil}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

export default Uccs;